import { useRef } from 'react'
import classNames from 'classnames/bind'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperType } from 'swiper/types'
import { Grid, Pagination } from 'swiper'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'

// Libs
import { usePopularProducts } from '../../Live/components/PopularZone/hooks/usePopularProducts'
import useMedia from '@/hooks/useMedia'
import { REC_SOURCE } from '@/methods/recommendTracking'

// Components
import ProductCard from '@/components/shared/ProductCard'
import HotSectionTitle from '../utils/HotSectionTitle'
import Navigation from '@/components/shared/Navigation'

// Constants
import { GtmItemListName } from '@/constants/gtmEvent'
import { ROUTE_PATH } from '@/constants/routePath'

// Styles
import styles from './style.module.scss'
import 'swiper/css/grid'

const cx = classNames.bind(styles)

// 點擊時送 select_item（item_list 用區塊本身）並記下來源，推薦成效才看得到首頁熱門商品帶來的點擊
const HOME_HOT_GTM_LIST = { itemListName: GtmItemListName.RECOMMEND_HOME_HOT, itemListId: `recommend_${REC_SOURCE.HOME_HOT}`, useListForItems: true }

const HotProducts = () => {
  const { t } = useTranslation()
  const swiperRef = useRef<SwiperType>()

  const { isMobile } = useMedia()
  const { popularProductsData } = usePopularProducts({ params: { event: 'home_hot_product' } })
  const products = popularProductsData?.productsInfo ?? []
  const showSlideButton = () => {
    if (!swiperRef.current) return true

    const slidesPerView = swiperRef.current.params.slidesPerView
    const gridRows = swiperRef.current.params.grid?.rows || 1
    const totalSlides = swiperRef.current.slides.length

    const slidesPerPage = (typeof slidesPerView === 'string' ? parseInt(slidesPerView) : slidesPerView || 0) * gridRows

    return totalSlides > slidesPerPage
  }

  const canSlide = showSlideButton()

  return (
    <div className={cx('hot-products')}>
      <HotSectionTitle className={cx('hot-products__title')}>
        {t('Popular_Product_Title')}
        <Link href={ROUTE_PATH.MALL} className={cx('hot-products__more-button')}>
          {t('Product_More')}
        </Link>
      </HotSectionTitle>
      {/* 設計稿：每列 2 / 4 / 5 / 6 個（<710 / 710 / 1024 / 1440），欄距 10；列距見 style */}
      <Swiper
        slidesPerView={2}
        slidesPerGroup={2}
        spaceBetween={10}
        breakpoints={{
          710: { slidesPerView: 4, slidesPerGroup: 4 },
          1024: { slidesPerView: 5, slidesPerGroup: 5 },
          1440: { slidesPerView: 6, slidesPerGroup: 6 },
        }}
        modules={[Grid, Pagination]}
        onSwiper={(swiper: any) => (swiperRef.current = swiper)}
        noSwiping
        noSwipingClass='no-swipe'
        grid={{
          rows: 2,
          fill: 'row',
        }}
        pagination={{
          clickable: true,
          el: '.pagination-container',
        }}
      >
        {products.map((product: any, index: number) => (
          <SwiperSlide key={index}>
            <ProductCard {...product} gtmItemEventParams={HOME_HOT_GTM_LIST} recSource={REC_SOURCE.HOME_HOT} />
          </SwiperSlide>
        ))}
      </Swiper>
      <div className={cx('hot-products-pagination')}>
        {!isMobile && canSlide && (
          <Navigation className={cx('hot-products-pagination-arrow')} direction='left' onClick={() => swiperRef.current?.slidePrev()} />
        )}
        <div
          className='swiper-pagination pagination-container'
          style={{ width: 'min-content', position: 'static', display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center' }}
        ></div>
        {!isMobile && canSlide && (
          <Navigation className={cx('hot-products-pagination-arrow')} direction='right' onClick={() => swiperRef.current?.slideNext()} />
        )}
      </div>
    </div>
  )
}

export default HotProducts
